/**
 * Utilities for applying granular changes to metric prompts
 */

import type { Change } from "./optimization"

interface ChangePosition {
  change: Change
  startIndex: number
  endIndex: number
}

/**
 * Find the position of a change's original text in the prompt
 */
function findChangePosition(
  prompt: string,
  change: Change,
): ChangePosition | null {
  // For additions (empty original text), we can't find a position
  if (!change.originalText) {
    return null
  }

  const startIndex = prompt.indexOf(change.originalText)
  if (startIndex === -1) {
    return null
  }

  return {
    change,
    startIndex,
    endIndex: startIndex + change.originalText.length,
  }
}

/**
 * Apply selected changes to the original prompt
 * Changes are applied in reverse order (from end to start) to preserve positions
 */
export function applySelectedChanges(
  originalPrompt: string,
  allChanges: Change[],
  selectedChangeIds: string[],
): string {
  // Filter to only selected changes
  const selectedChanges = allChanges.filter((c) =>
    selectedChangeIds.includes(c.id),
  )

  if (selectedChanges.length === 0) {
    return originalPrompt
  }

  // Separate additions (no original text) from replacements
  const additions = selectedChanges.filter((c) => !c.originalText)
  const replacements = selectedChanges.filter((c) => c.originalText)

  // Find positions for replacements
  const positions: ChangePosition[] = []
  for (const change of replacements) {
    const position = findChangePosition(originalPrompt, change)
    if (position) {
      positions.push(position)
    }
  }

  // Sort by position (reverse order - apply from end to start)
  positions.sort((a, b) => b.startIndex - a.startIndex)

  // Apply replacements
  let result = originalPrompt
  for (const pos of positions) {
    result =
      result.substring(0, pos.startIndex) +
      pos.change.newText +
      result.substring(pos.endIndex)
  }

  // Handle additions - append at end or at specified location
  // For now, we'll append additions with a note
  if (additions.length > 0) {
    for (const addition of additions) {
      // Try to find a logical place to insert
      // If the location description mentions specific areas, try to find them
      const location = addition.location.toLowerCase()

      if (location.includes("yes") && location.includes("condition")) {
        // Try to insert after "Return YES if" section
        const yesIndex = result.indexOf("Return YES if")
        if (yesIndex !== -1) {
          // Find the next blank line or "Return NO"
          const nextSection = result.indexOf("Return NO", yesIndex)
          if (nextSection !== -1) {
            result = `${
              result.substring(0, nextSection) + addition.newText
            }\n${result.substring(nextSection)}`
            continue
          }
        }
      }

      if (location.includes("no") && location.includes("condition")) {
        // Try to insert after "Return NO if" section
        const noIndex = result.indexOf("Return NO if")
        if (noIndex !== -1) {
          // Find the next section or end
          const nextSection = result.indexOf("\n\n", noIndex)
          if (nextSection !== -1) {
            result = `${result.substring(0, nextSection)}\n${
              addition.newText
            }${result.substring(nextSection)}`
            continue
          }
        }
      }

      // Default: append at end
      result = `${result}\n${addition.newText}`
    }
  }

  return result
}

/**
 * Generate a unique ID for a change
 */
export function generateChangeId(): string {
  return `change_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
}
